/**
 * The rolling-stock register — pure model (Chris, 2026-09-11).
 *
 * The register is the railway's fleet as named engines: owned or contracted,
 * steam / diesel / railcar, coal or oil, WHITBY ENABLED or not, and the shape
 * of each hire contract. It is edited on the Brief ("Rolling stock in use") and
 * stored in app.StudioLoco (db/83); this file holds the arithmetic that turns it
 * into (1) the Brief's fleet counts, (2) the named hire lines on the Cost model
 * stage, and (3) the "Fixed Rolling Stock" spread that pulls the contracted
 * minimums out of the P&L hire line.
 *
 * Contract truths Chris settled (2026-09-11):
 *   - a chargeable mile is a TIMETABLED mile — the engine's passenger workings;
 *     the model's empty-stock positioning moves carry no hire;
 *   - NYMR pays maintenance, coal, oil and crew, which already sit in the P&L
 *     lines (fuel, traction & rolling stock, MPD/operations labour) — so a hire
 *     rate here is PURE hire and never double-counts them;
 *   - a failed or stood-down engine is swapped for a diesel: no timetabled
 *     miles, no per-mile hire that day; its contracted minimum is still owed.
 *
 * Every figure derived here is labelled: an engine whose basis-season miles the
 * COO has not supplied is estimated from the timetable the railway ran.
 */
import type { FleetCaps, FleetLoco, Traction } from './types'
import type { Activity, CostLine, RollingStockConfig } from './costLines'

/** The engines the planner has ticked "Use in simulation" — the fleet the build may use. */
export const inSimulation = (locos: FleetLoco[] | undefined): FleetLoco[] => (locos ?? []).filter((l) => l.inSimulation)

/** Stable line/engine key — the register id as text. */
export const engineKey = (l: Pick<FleetLoco, 'id'>): string => String(l.id)

/** "92134" or "31128 Charybdis" — how every surface names an engine. */
export const engineLabel = (l: Pick<FleetLoco, 'number' | 'name'>): string => (l.name ? `${l.number} ${l.name}` : l.number)

/**
 * The Brief's fleet with the register applied. The planner's counts (Fleet &
 * sets) say how many engines of each traction are OUT ON A DAY; the register
 * says which engines exist. The register CONSTRAINS those counts and never
 * raises them — a count above the ticked engines of a traction would be an
 * engine that does not exist (the Brief's fleet of three steam stays three
 * with six steam engines ticked; it becomes two if only two are ticked). The
 * ECS slot counts stock moves, not engines, so it is the planner's. Returns
 * null when the register has no engine ticked, and the plain counts stand.
 */
export function fleetCapsFrom(locos: FleetLoco[] | undefined, current: FleetCaps): FleetCaps | null {
  const use = inSimulation(locos)
  if (use.length === 0) return null
  const n = (t: Traction) => use.filter((l) => l.traction === t).length
  return {
    ...current,
    maxSteam: Math.min(current.maxSteam, n('steam')),
    maxDiesel: Math.min(current.maxDiesel, n('diesel')),
    maxRailcar: Math.min(current.maxRailcar, n('railcar')),
    locos: use,
  }
}

/** A month's running-day count, operating-month indexed (March = 0 … February = 11). */
export type MonthDays = number[]

/**
 * Spread a season £ across the months that have running days, in equal shares,
 * then apply the planner's per-month cells. Never negative. Months with no
 * running days take nothing unless the planner types a figure into them.
 */
export function spreadAcrossMonths(total: number, runningDays: MonthDays, cells?: Record<number, number>): number[] {
  const out = Array(12).fill(0) as number[]
  const running = runningDays.map((d, i) => (d > 0 ? i : -1)).filter((i) => i >= 0)
  if (total > 0 && running.length > 0) {
    const share = Math.round((total / running.length) * 100) / 100
    for (const i of running) out[i] = share
    // Put the rounding pennies on the last running month so the season adds up.
    const sum = out.reduce((a, b) => a + b, 0)
    out[running[running.length - 1]] = Math.round((out[running[running.length - 1]] + (total - sum)) * 100) / 100
  }
  if (cells) for (const [k, v] of Object.entries(cells)) { const i = Number(k); if (i >= 0 && i < 12 && Number.isFinite(v)) out[i] = Math.max(0, v) }
  return out
}

/**
 * ONE engine's hire, month by month, from its contract and the miles/days it
 * works (`milesByMonth` are its TIMETABLED miles; `daysByMonth` the operating
 * days it has a diagram):
 *   fixed    = fixed annual cost + contracted minimum × cost per mile
 *              (payable regardless — spread equally across the months the
 *              engine is active, i.e. has running days)
 *   variable = cost per mile × the miles ABOVE the contracted minimum
 *              (apportioned to months by where the miles fell)
 *            + day rate × operating days worked
 *            + maintenance per mile × miles (only when the planner enters one;
 *              NYMR's maintenance is otherwise already in the P&L lines)
 */
/**
 * An engine's hire on given miles and days. PAID ON THE MILES IT RUNS (Chris,
 * 2026-10-28, rebuild item 2: "the fixed cost … is not automatically going to
 * be paid. It will only be paid for the miles run"): £/mile × every timetabled
 * mile + the day rate × days worked + any maintenance £/mile. The contracted
 * minimum is no longer a fixed charge — it is the roster's PRIORITY
 * (engineRoster.ts gives contract engines their miles first, spread over the
 * season). Only a genuine flat fee (fixedAnnualCost) stays FIXED.
 */
export function engineHire(l: FleetLoco, milesByMonth: number[], daysByMonth: number[], runningDays: MonthDays): { fixed: number[]; variable: number[]; fixedSeason: number } {
  const perMile = l.costPerMile ?? 0
  const fixedSeason = l.fixedAnnualCost ?? 0
  const fixed = spreadAcrossMonths(fixedSeason, runningDays)
  const variable = Array(12).fill(0) as number[]
  for (let i = 0; i < 12; i++) {
    const m = milesByMonth[i] ?? 0
    variable[i] = Math.round((perMile * m + (l.dayRate ?? 0) * (daysByMonth[i] ?? 0) + (l.maintenancePerMile ?? 0) * m) * 100) / 100
  }
  return { fixed, variable, fixedSeason }
}

/** An engine's basis-season miles by month: the COO's figure or the contracted
 *  minimum laid on the timetable's month shape, else the timetable's estimate.
 *  ONE owner for the hire lines and the fuel lines. */
export function engineBasisMiles(l: FleetLoco, basisEngines: EngineActivity | undefined, runningDays: MonthDays): number[] {
  let miles = basisEngines?.miles[engineKey(l)] ?? (Array(12).fill(0) as number[])
  const known = basisMilesAssumed(l)
  if (known != null) {
    const shape = miles.reduce((t, v) => t + v, 0)
    miles = shape > 0 ? miles.map((m) => Math.round((m / shape) * known * 100) / 100) : spreadAcrossMonths(known, runningDays)
  }
  return miles
}

/**
 * An engine's basis-season miles as the register knows them: the COO's figure
 * when entered; else the contracted minimum (the railway pays for those miles
 * whether or not they were run — with no mileage return that is the honest
 * floor, Chris 2026-09-11); null = nothing known, estimate from the timetable.
 */
export function basisMilesAssumed(l: Pick<FleetLoco, 'basisMiles' | 'minContractMiles'>): number | null {
  if (l.basisMiles != null && l.basisMiles > 0) return l.basisMiles
  if (l.minContractMiles != null && l.minContractMiles > 0) return l.minContractMiles
  return null
}

/** The P&L line the register's hire comes out of. */
export const HIRE_LINE_LABEL = 'Hire of Rolling Stock'
export const ROLLING_STOCK_CATEGORY = 'Rolling stock'
export const RS_SPREAD_ID = 'rs:fixed-spread'
export const RS_MEMO_ID = 'rs:hire-pnl'
export const rsEngineFixedId = (key: string) => `rs:engine:${key}:fixed`
export const rsEngineVarId = (key: string) => `rs:engine:${key}:variable`
/** The P&L line an engine's own fuel comes out of (db/125). */
export const FUEL_LINE_LABEL = 'Fuel, Oils and Lubricants'
export const LOCO_CATEGORY = 'Locomotives'
export const FUEL_MEMO_ID = 'fuel:pnl'
export const engineFuelId = (key: string) => `fuel:engine:${key}`

/** Miles and days per engine per month, both sides — what the register's lines are priced on. */
export interface EngineActivity {
  /** engine key → 12 months of TIMETABLED miles (basis = today's timetable, estimated or the COO's figure). */
  miles: Record<string, number[]>
  /** engine key → 12 months of operating days worked. */
  days: Record<string, number[]>
}

/**
 * THE rolling-stock lines for the Cost model stage (Chris, 2026-09-11):
 *
 *  1. "Fixed Rolling Stock" — the planner's Spread, a FIXED line across the running
 *     months (cells editable), deducted from the P&L hire line;
 *  2. one FIXED and one VARIABLE line per ticked engine, named, under Hire of
 *     Rolling Stock — its contract's commitment and its per-mile/day cost on the
 *     basis season's miles — both deducted from the P&L hire line;
 *  3. the P&L hire line itself becomes "Variable after Fixed": what remains,
 *     never below zero, still variable by working turns; a memo line shows the
 *     P&L figure before the deductions (excluded from totals).
 *
 * The engines are deducted first; the Spread applies to what remains. Warnings
 * name any month where the deductions exceed the P&L hire.
 */
export function applyRollingStock(
  lines: CostLine[],
  cfg: RollingStockConfig | undefined,
  locos: FleetLoco[] | undefined,
  runningDays: MonthDays,
  basisEngines: EngineActivity | undefined,
): { lines: CostLine[]; warnings: string[]; deductions: number[] } {
  const use = inSimulation(locos)
  const spread = cfg?.spread ?? 0
  const warnings: string[] = []
  const hire = lines.find((l) => l.label === HIRE_LINE_LABEL && l.kind === 'expense')
  if (!hire) return { lines, warnings, deductions: Array(12).fill(0) as number[] }

  const zeros = () => Array(12).fill(0) as number[]
  const added: CostLine[] = []
  const deductions = zeros()
  // 1. named engines first — each engine's basis-season miles: the COO's figure
  //    when the planner entered one; else the CONTRACTED MINIMUM (Chris,
  //    2026-09-11: no COO mileage return — "use the minimums and let us tweak
  //    the rest"); else the miles estimated from the timetable the railway ran.
  //    The timetable gives the month shape in every case.
  for (const l of use) {
    const key = engineKey(l)
    const miles = engineBasisMiles(l, basisEngines, runningDays)
    const days = basisEngines?.days[key] ?? zeros()
    const h = engineHire(l, miles, days, runningDays)
    const hasFixed = h.fixedSeason > 0
    const hasVar = h.variable.some((v) => v > 0) || (l.costPerMile ?? 0) > 0 || (l.dayRate ?? 0) > 0 || (l.maintenancePerMile ?? 0) > 0
    if (hasFixed) {
      added.push({
        id: rsEngineFixedId(key), kind: hire.kind, category: LOCO_CATEGORY,
        label: `${engineLabel(l)} — fixed fee`,
        months: h.fixed, behaviour: 'fixed', driver: 'runningDays', included: true, engineId: key, parentId: hire.id,
      })
    }
    if (hasVar) {
      added.push({
        id: rsEngineVarId(key), kind: hire.kind, category: LOCO_CATEGORY,
        label: `${engineLabel(l)} — hire on miles run${(l.dayRate ?? 0) > 0 ? ' and days' : ''}${(l.minContractMiles ?? 0) > 0 ? ` (contract ${(l.minContractMiles ?? 0).toLocaleString('en-GB')} mi, rostered first)` : ''}`,
        months: h.variable, behaviour: 'variable', driver: 'trainMiles', included: true, engineId: key, parentId: hire.id,
        engineContract: { costPerMile: l.costPerMile ?? 0, dayRate: l.dayRate ?? 0, minContractMiles: l.minContractMiles ?? 0, maintenancePerMile: l.maintenancePerMile ?? 0 },
      })
    }
    for (let i = 0; i < 12; i++) deductions[i] += h.fixed[i] + h.variable[i]
  }
  // 2. "Fixed Rolling Stock" — the planner's Spread on what remains. The line is
  //    always shown (its Spread cell is where the figure is typed); at £0 it
  //    deducts nothing.
  const spreadMonths = spreadAcrossMonths(spread, runningDays, cfg?.spreadMonths)
  added.push({ id: RS_SPREAD_ID, kind: hire.kind, category: ROLLING_STOCK_CATEGORY, label: 'Fixed Rolling Stock', months: spreadMonths, behaviour: 'fixed', driver: 'runningDays', included: true, parentId: hire.id })
  for (let i = 0; i < 12; i++) deductions[i] += spreadMonths[i]
  // 3. the hire line after deductions, floored at zero and warned
  const after = hire.months.map((v, i) => Math.max(0, Math.round((v - deductions[i]) * 100) / 100))
  const MONTHS = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb']
  for (let i = 0; i < 12; i++) {
    if (deductions[i] > hire.months[i] + 0.5) warnings.push(`${MONTHS[i]}: the fixed rolling stock and named engines (£${Math.round(deductions[i]).toLocaleString('en-GB')}) exceed the P&L hire (£${Math.round(hire.months[i]).toLocaleString('en-GB')}) — the variable line is held at £0 there`)
  }
  // The memo keeps the P&L figure in view (never in totals); the residual is
  // the hire line itself, after the deductions, still variable by its driver.
  const memo: CostLine = { ...hire, id: RS_MEMO_ID, category: ROLLING_STOCK_CATEGORY, label: `${HIRE_LINE_LABEL} (P&L, before the deductions below)`, included: false, rsMemo: true, memoOf: hire.id }
  const residual: CostLine = { ...hire, category: ROLLING_STOCK_CATEGORY, label: 'Variable after Fixed — hire of rolling stock', months: after, rsResidual: true, residualOf: hire.id }
  // All of it sits where the hire line was: memo, the named engines, Fixed
  // Rolling Stock, then Variable after Fixed (Chris: "a line under Hire of
  // Rolling Stock").
  const at = lines.findIndex((l) => l.id === hire.id)
  const out = [...lines.slice(0, at), memo, ...added, residual, ...lines.slice(at + 1)]
  return { lines: out, warnings, deductions }
}

/** Sum an EngineActivity into per-month totals for a set of engine keys (for checks). */
export const seasonMiles = (a: EngineActivity | undefined, key: string): number => (a?.miles[key] ?? []).reduce((t, v) => t + v, 0)

/** The register's months of activity into the cost model's Activity vector (per month). */
export function withEngineActivity(activity: Activity[], eng: EngineActivity | undefined): Activity[] {
  if (!eng) return activity
  return activity.map((a, i) => {
    const engineMiles: Record<string, number> = {}
    const engineDays: Record<string, number> = {}
    for (const [k, v] of Object.entries(eng.miles)) engineMiles[k] = v[i] ?? 0
    for (const [k, v] of Object.entries(eng.days)) engineDays[k] = v[i] ?? 0
    return { ...a, engineMiles, engineDays }
  })
}

/**
 * FUEL & OILS PER LOCO (Chris, 2026-10-28, rebuild item 4). Every ticked engine
 * with its own fuel £/mile gets a VARIABLE line "<engine> — fuel & oils" =
 * its timetabled miles × that rate (basis: its basis miles; modelled: the miles
 * the roster gives it — priced like a contract line), and the same £ comes off
 * the P&L "Fuel, Oils and Lubricants" line, which carries on (variable by
 * train-miles) for every other engine. A memo keeps the P&L figure in view,
 * never in the totals. No engine with a fuel rate → the lines are untouched.
 */
export function applyEngineFuel(
  lines: CostLine[],
  locos: FleetLoco[] | undefined,
  runningDays: MonthDays,
  basisEngines: EngineActivity | undefined,
): { lines: CostLine[]; warnings: string[] } {
  const fuelled = inSimulation(locos).filter((l) => (l.fuelPerMile ?? 0) > 0)
  const fuel = lines.find((l) => l.label === FUEL_LINE_LABEL && l.kind === 'expense')
  if (!fuel || fuelled.length === 0) return { lines, warnings: [] }
  const added: CostLine[] = []
  const deductions = Array(12).fill(0) as number[]
  for (const l of fuelled) {
    const key = engineKey(l)
    const rate = l.fuelPerMile ?? 0
    const months = engineBasisMiles(l, basisEngines, runningDays).map((m) => Math.round(m * rate * 100) / 100)
    added.push({
      id: engineFuelId(key), kind: fuel.kind, category: LOCO_CATEGORY,
      label: `${engineLabel(l)} — fuel & oils`, months, behaviour: 'variable', driver: 'trainMiles', included: true,
      engineId: key, parentId: fuel.id,
      // Priced on the engine's own miles, like its hire (costLines.ts::simulateLine).
      engineContract: { costPerMile: rate, dayRate: 0, minContractMiles: 0, maintenancePerMile: 0 },
    })
    for (let i = 0; i < 12; i++) deductions[i] += months[i]
  }
  const MONTHS = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb']
  const warnings: string[] = []
  for (let i = 0; i < 12; i++) {
    if (deductions[i] > fuel.months[i] + 0.5) warnings.push(`${MONTHS[i]}: the engines' own fuel (£${Math.round(deductions[i]).toLocaleString('en-GB')}) exceeds the P&L fuel line (£${Math.round(fuel.months[i]).toLocaleString('en-GB')}) — the other engines' fuel is held at £0 there`)
  }
  const memo: CostLine = { ...fuel, id: FUEL_MEMO_ID, label: `${FUEL_LINE_LABEL} (P&L, before the engines' own fuel below)`, included: false, memoOf: fuel.id }
  const residual: CostLine = { ...fuel, label: `${FUEL_LINE_LABEL} — other engines`, months: fuel.months.map((v, i) => Math.max(0, Math.round((v - deductions[i]) * 100) / 100)), residualOf: fuel.id }
  const at = lines.findIndex((x) => x.id === fuel.id)
  return { lines: [...lines.slice(0, at), memo, ...added, residual, ...lines.slice(at + 1)], warnings }
}
